/**
 * Types for Knowledge Base UI Components
 */

import type {
  KnowledgeBaseDocumentSourceType,
  KnowledgeBaseFolderSourceType,
} from '@/lib/ai/knowledge-base/folder-types'

export interface KBDocument {
  id: string
  tenant_id: string
  title: string
  content: string
  content_type: string
  source_url?: string
  source_type?: KnowledgeBaseDocumentSourceType
  folder_id?: string | null
  metadata: KBDocumentMetadata
  tags: string[]
  category?: string
  is_active: boolean
  is_default: boolean
  injection_mode: 'direct' | 'rag' | 'auto'
  priority: number
  embedding_status: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped'
  embedding_error?: string
  token_count?: number
  last_embedded_at?: string
  created_at: string
  updated_at: string
  embedding_count?: number
  /** Tenant name (only when viewing all tenants as super_user) */
  tenant_name?: string
  /** File download URL (if original file is stored) */
  file_url?: string
}

export type KBFolderSourceType = KnowledgeBaseFolderSourceType
export type KBSourceType = KnowledgeBaseDocumentSourceType

export interface DocumentFormState {
  title: string
  content: string
  content_type: string
  source_url: string
  source_type: NonNullable<KnowledgeBaseDocumentSourceType>
  category: string
  tags: string[]
  injection_mode: 'direct' | 'rag' | 'auto'
  priority: number
  is_active: boolean
  /** When true, auto-include in all new agents and apply-to-all on creation */
  is_default: boolean
  /** AI summary for spreadsheet documents (stored in metadata.spreadsheet.ai_summary) */
  ai_summary?: string
  /** Original metadata (for preserving spreadsheet metadata on edit) */
  metadata?: KBDocumentMetadata
}

export interface SearchResult {
  id: string
  documentId: string
  chunkIndex: number
  chunkText: string
  chunkMetadata: SearchResultMetadata
  similarityScore: number
  documentTitle: string
  documentCategory?: string
  documentTags: string[]
}

export interface KBDocumentMetadata extends Record<string, unknown> {
  ai_summary?: string
  file_path?: string
  original_filename?: string
  original_size?: number
  original_type?: string
  source_url?: string
  spreadsheet?: {
    ai_summary?: string
    [key: string]: unknown
  }
}

export interface SearchResultMetadata extends Record<string, unknown> {
  matchType?: string
}

export interface Inbox {
  id: string
  address: string
}

export const DEFAULT_DOCUMENT_FORM: DocumentFormState = {
  title: '',
  content: '',
  content_type: 'text/plain',
  source_url: '',
  source_type: 'manual',
  category: '',
  tags: [],
  injection_mode: 'auto',
  priority: 100,
  is_active: true,
  is_default: true,
  ai_summary: undefined,
  metadata: undefined,
}

export const INJECTION_MODE_OPTIONS = [
  { value: 'auto', label: 'Auto', description: 'System decides based on size' },
  { value: 'direct', label: 'Direct Injection', description: 'Inject into system prompt (small docs)' },
  { value: 'rag', label: 'RAG', description: 'Semantic search with embeddings (large docs)' },
]

export const EMBEDDING_STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  processing: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  skipped: 'bg-gray-100 text-gray-800',
}

// ============================================================================
// File Upload Types
// ============================================================================

/** Supported file types for upload (text-based) */
export const SUPPORTED_FILE_TYPES = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'text/plain': ['.txt'],
  'text/markdown': ['.md'],
} as const

/** Spreadsheet file types (handled separately with preview) */
export const SPREADSHEET_FILE_TYPES = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls'],
  'text/csv': ['.csv'],
  'application/csv': ['.csv'],
} as const

/** All supported file types combined */
export const ALL_SUPPORTED_FILE_TYPES = {
  ...SUPPORTED_FILE_TYPES,
  ...SPREADSHEET_FILE_TYPES,
} as const

/** Check if file is a spreadsheet */
export function isSpreadsheetFile(file: File): boolean {
  const mimeType = file.type
  const ext = file.name.split('.').pop()?.toLowerCase()

  if (mimeType in SPREADSHEET_FILE_TYPES) return true
  if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') return true

  return false
}

/** Maximum file size in bytes (10MB) */
export const MAX_FILE_SIZE = 10 * 1024 * 1024

/** File upload response from API */
export interface FileUploadResponse {
  document: KBDocument
  parsed: {
    wordCount: number
    charCount: number
    pageCount?: number
    tokenCount: number
  }
}

/** Document status response from API (for polling) */
export interface DocumentStatusResponse {
  id: string
  embedding_status: KBDocument['embedding_status']
  embedding_error?: string
  token_count?: number
  embedding_count: number
  updated_at: string
}
